import type { ReactElement } from 'react';
import { Skeleton } from '@/components/ui';
import type { VisaTutorResponse } from '@/lib/types';
import { BarList, FunnelChart, type BarListRow, type FunnelStage } from '../fab-ui';
import { PiSection, PiStat, PiStatGrid } from '../pi-kit';
import { formatCount, formatRate } from '../use-fab-data';
import { countryLabel, formatScore, formatSignedPercent } from './visa-tutor-model';

/**
 * AI Visa Tutor headline, split into the parts the page lays out (layout
 * manager, 2026-10-20: the tiles stay fixed, the three panels are movable):
 * - `VisaTutorStats`: four tiles (Clients, Sessions, Visa approval rate,
 *   Avg improvement).
 * - `VisaTutorFunnel`: the licence funnel
 *   Licences → Allocated → Activated → Practising → Outcomes reported → Approved
 *   (the licence counts live only there, never repeated as tiles).
 * - `VisaTutorScores`: the average scores (each "x / 100").
 * - `VisaTutorDestinations`: the destination-country list.
 *
 * Every figure is the server's `overall` for the cycle (search on the table
 * never changes these). Licence counts are inventory at a point in time; the
 * rest is activity in the window. Errors are rendered by the page.
 *
 * A destination row is a button when `onSelectCountry` is passed and the row
 * carries an ISO code; the page narrows the whole read to that country.
 */

interface VisaTutorPartProps {
  data: VisaTutorResponse | null;
  loading: boolean;
}

export function VisaTutorStats({ data, loading }: VisaTutorPartProps): ReactElement {
  const overall = data?.data.overall ?? null;
  const busy = loading && data === null;
  const detail = (content: string) => (busy || overall === null ? undefined : content);

  return (
    <PiStatGrid>
      <PiStat label="Clients" value={formatCount(overall?.clients)} loading={busy} />
      <PiStat
        label="Sessions"
        value={formatCount(overall?.sessions)}
        detail={detail(`${formatCount(overall?.sessionsCompleted)} completed`)}
        loading={busy}
      />
      <PiStat
        label="Visa approval rate"
        value={formatRate(overall?.approvalRate ?? null)}
        detail={detail(`${formatCount(overall?.rejected)} rejected`)}
        loading={busy}
      />
      <PiStat
        label="Avg improvement"
        value={formatSignedPercent(overall?.avgImprovementPct)}
        detail={detail(`${formatCount(overall?.improvementStudents)} students`)}
        loading={busy}
      />
    </PiStatGrid>
  );
}

export function VisaTutorFunnel({ data, loading }: VisaTutorPartProps): ReactElement {
  const overall = data?.data.overall ?? null;
  const busy = loading && data === null;

  const stages: FunnelStage[] = [
    { key: 'licences', label: 'Licences', value: overall?.licencesTotal ?? null },
    { key: 'allocated', label: 'Allocated', value: overall?.licencesAllocated ?? null },
    { key: 'activated', label: 'Activated', value: overall?.licencesActivated ?? null },
    { key: 'practising', label: 'Practising', value: overall?.studentsPractising ?? null },
    { key: 'outcomes', label: 'Outcomes reported', value: overall?.outcomesReported ?? null },
    { key: 'approved', label: 'Approved', value: overall?.approved ?? null },
  ];

  return (
    <PiSection title="Licence funnel">
      {/* Six stages need ~56rem side by side; FunnelChart switches to its row
          layout at `md`, so between md and that width the funnel scrolls
          inside its own box rather than crushing the figures. */}
      <div className="min-w-0 overflow-x-auto">
        <div className="md:min-w-[56rem]">
          <FunnelChart stages={stages} loading={busy} />
        </div>
      </div>
    </PiSection>
  );
}

export function VisaTutorScores({ data, loading }: VisaTutorPartProps): ReactElement {
  const overall = data?.data.overall ?? null;
  const busy = loading && data === null;

  // Body language averages only scored sessions upstream; its count shows
  // when the API sends it (undefined = an older API, null = not reported).
  const scoredSessions = overall?.bodyLanguageScoredSessions;
  const scores: { key: string; label: string; value: number | null | undefined; fact?: string }[] = [
    { key: 'overall', label: 'Overall', value: overall?.avgOverallScore },
    { key: 'communication', label: 'Communication', value: overall?.avgCommunicationScore },
    {
      key: 'body-language',
      label: 'Body language',
      value: overall?.avgBodyLanguageScore,
      ...(scoredSessions !== undefined && scoredSessions !== null
        ? {
            fact: `${formatCount(scoredSessions)} scored ${scoredSessions === 1 ? 'session' : 'sessions'}`,
          }
        : {}),
    },
    { key: 'visa', label: 'Visa', value: overall?.avgVisaScore },
  ];

  return (
    <PiSection title="Average scores">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
        {scores.map((score) => (
          <div key={score.key} className="min-w-0">
            <dt className="text-xs text-fg-muted">{score.label}</dt>
            <dd className="mt-0.5 text-lg font-semibold text-fg tabular-nums">
              {busy ? <Skeleton className="h-6 w-20" /> : formatScore(score.value)}
            </dd>
            {!busy && score.fact !== undefined && (
              <dd className="text-xs text-fg-muted tabular-nums">{score.fact}</dd>
            )}
          </div>
        ))}
      </dl>
    </PiSection>
  );
}

export function VisaTutorDestinations({
  data,
  loading,
  onSelectCountry,
}: VisaTutorPartProps & {
  onSelectCountry?: (iso2: string, label: string) => void;
}): ReactElement {
  const busy = loading && data === null;

  const countryRows: BarListRow[] = (data?.data.byCountry ?? [])
    .filter((row) => row.allocations > 0)
    .slice()
    .sort((a, b) => b.allocations - a.allocations || countryLabel(a).localeCompare(countryLabel(b)))
    .map((row, index) => {
      const label = countryLabel(row);
      const code = row.countryCode?.trim() ?? '';
      const hint =
        row.outcomesReported > 0
          ? `${formatCount(row.approved)} approved · ${formatCount(row.rejected)} rejected`
          : undefined;
      return {
        key: code !== '' ? code : `row:${index}`,
        label,
        value: row.allocations,
        ...(hint !== undefined ? { hint } : {}),
        ...(onSelectCountry !== undefined && code !== ''
          ? { onSelect: () => onSelectCountry(code, label) }
          : {}),
      };
    });

  return (
    <BarList
      title="Licences by destination country"
      rows={countryRows}
      loading={busy}
      emptyText="No licences allocated to a destination."
      maxRows={8}
    />
  );
}
